import _ from "lodash";
/** External */
import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import {
  SearchInput,
  Dropdown,
  DropdownMenu,
  FilterLabelButton,
} from "@toddle-edu/ds-web";

/** Internal */
import { useI18n } from "Hooks";
import { compose, graphql } from "lib/apolloClient";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  isDpGrade,
  getOrganizationGradeOptions,
} from "ToddleLibrary/modules/ToddleLibraryUtils";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import ACLStore from "lib/aclStore";

/** Relative */
import classes from "./CourseFilters.scss";
import {
  getOrganizationGradesQuery,
  getOrganizationSubjectsQuery,
} from "./modules/CourseFilterQueries";
import {
  getOrganizationGradesFromCache,
  getOrganizationSubjectsFromCache,
} from "./modules/CourseFilterGraphqlHelpers";
import { getSubjectOptionsMemoized } from "./modules/Utils";
import { ENABLE_GLOBAL_HEADER_ON_MULTIPLE_SCREENS } from "Constants/permissionConstants";

const CourseFilters = ({
  grades,
  subjects,
  subjectOptions,
  searchInput,
  selectedGradeId,
  onSearchTextChange,
  onChangeGradeId,
  selectedSubjectId,
  onChangeSubjectId,
  gradeLabel,
  selectedGlobalGradeUid,
  shouldDisplaySubjectsFilter,
  isGlobalHeaderOnMultipleScreensEnabled,
  setSubjects,
}) => {
  const { t } = useI18n();
  const gradeOptions = getOrganizationGradeOptions(grades);
  const previousSelectedGlobalGradeUidRef = useRef(null);

  // subject options required in curriculumCoursesQueryVariables for DP curriculum
  useEffect(() => {
    setSubjects(subjects);
  }, [subjects]);

  const getGradeFromGlobalGradeUid = globalGradeUid => {
    if (globalGradeUid === "all") return { id: "all" };

    return _.find(grades, grade => grade?.globalGrade?.uid === globalGradeUid);
  };

  const getGradeFromGradeId = gradeId =>
    _.find(grades, grade => grade.id === gradeId);

  const handleGradeUpdate = (gradeId, globalGradeUid) => {
    onChangeGradeId(gradeId);
    previousSelectedGlobalGradeUidRef.current = globalGradeUid;
  };

  const setGradeByGlobalGradeUid = globalGradeUid => {
    const defaultGradeOption = getGradeFromGlobalGradeUid(globalGradeUid);

    if (defaultGradeOption) {
      handleGradeUpdate(
        _.get(defaultGradeOption, "id"),
        _.get(defaultGradeOption, "globalGrade.uid")
      );
    } else {
      handleGradeUpdate("all", null);
    }
  };

  const makeDefaultGradeSelection = () => {
    if (isDpGrade(gradeLabel)) {
      handleGradeUpdate("all", null);
      return;
    }

    const previousSelectedGrade = getGradeFromGlobalGradeUid(
      previousSelectedGlobalGradeUidRef.current
    );

    if (previousSelectedGrade) {
      onChangeGradeId(previousSelectedGrade.id);
    } else if (selectedGlobalGradeUid) {
      setGradeByGlobalGradeUid(selectedGlobalGradeUid);
    } else {
      handleGradeUpdate("all", null);
    }
  };

  useEffect(() => {
    if (!_.isEmpty(grades)) makeDefaultGradeSelection();
  }, [grades]);

  const handleGradeChange = selectedGrade => {
    const selectedGradeId = _.get(selectedGrade, "key");

    const currentGrade = getGradeFromGradeId(selectedGradeId);
    previousSelectedGlobalGradeUidRef.current = currentGrade
      ? _.get(currentGrade, "globalGrade.uid")
      : "all";

    onChangeGradeId(selectedGradeId);
  };

  const handleSubjectChange = ({ key }) => {
    onChangeSubjectId(key);
  };

  const getFilterLabelValue = ({ key, type }) => {
    const option = _.find(type === "GRADE" ? gradeOptions : subjectOptions, {
      key,
    });
    return _.get(option, "label");
  };

  const selectedGradeLabel = getFilterLabelValue({
    key: selectedGradeId,
    type: "GRADE",
  });

  const gradeDropdownComponent = (
    <Dropdown
      overlay={
        <DropdownMenu
          options={gradeOptions}
          onClick={handleGradeChange}
          showSelection
          selectionType="tick"
          value={selectedGradeId}
          testId="appComponents-courseSelection-courseFilters-gradeDropdown"
          dsVersion="2.0"
        />
      }
    >
      <FilterLabelButton
        dsVersion="2.0"
        variant="neutral"
        type="plain"
        labelValue={selectedGradeLabel}
        size="medium"
        label={t("common:grade")}
        testId="appComponents-courseSelection-courseFilters-gradeDropdown"
      />
    </Dropdown>
  );

  const selectedSubjectLabel = getFilterLabelValue({
    key: selectedSubjectId,
    type: "SUBJECT",
  });

  const subjectDropdownComponent = (
    <Dropdown
      overlay={
        <DropdownMenu
          options={subjectOptions}
          onClick={handleSubjectChange}
          showSelection
          selectionType="tick"
          value={selectedSubjectId}
          dsVersion="2.0"
          testId="appComponents-courseSelection-courseFilters-subjectDropdown"
        />
      }
    >
      <FilterLabelButton
        labelValue={selectedSubjectLabel}
        size="medium"
        dsVersion="2.0"
        variant="neutral"
        type="plain"
        label={t("common:subject")}
        testId="appComponents-courseSelection-courseFilters-subjectDropdown"
      />
    </Dropdown>
  );

  return (
    <div className={classes.container}>
      <div className={classes.searchContainer}>
        <SearchInput
          value={searchInput}
          placeholder={t("common:search_placeholder")}
          size={"medium"}
          onChange={onSearchTextChange}
          testId="appComponents-courseSelection-courseFilters"
          dsVersion={isGlobalHeaderOnMultipleScreensEnabled ? "2.0" : "1.0"}
          focusSearchBySlash={isGlobalHeaderOnMultipleScreensEnabled}
        />
      </div>

      <div className={classes.filtersContainer}>
        {shouldDisplaySubjectsFilter && !_.isEmpty(subjects)
          ? subjectDropdownComponent
          : null}
        {!_.isEmpty(grades) ? gradeDropdownComponent : null}
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { selectedCurriulum, courseSubjectType } = ownProps;
  const gradeLabel = _.get(state, "toddleLibrary.globalFilter.grade.label", "");

  const platformUserInfo = getUserInfo({ portalType: "PLANNER" });
  const organizationId = _.get(platformUserInfo, "org_id");

  const selectedCurriulumId = _.get(selectedCurriulum, "id");
  const curriculumProgramIds = selectedCurriulumId ? [selectedCurriulumId] : [];

  const subjectQueryFilters = {
    curriculumProgramIds,
    types: courseSubjectType ?? [],
  };
  const isGlobalHeaderOnMultipleScreensEnabled = ACLStore.can(
    ENABLE_GLOBAL_HEADER_ON_MULTIPLE_SCREENS
  );
  return {
    gradeLabel,
    organizationId,
    curriculumProgramIds,
    subjectQueryFilters,
    isGlobalHeaderOnMultipleScreensEnabled,
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getOrganizationGradesQuery, {
    name: "getOrganizationGrades",
    skip: ({ curriculumProgramIds }) => _.isEmpty(curriculumProgramIds),
    options: ({ organizationId, curriculumProgramIds }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: organizationId,
        filters: { curriculumProgramIds },
        portalType: "PLANNER",
      },
    }),
    props({ getOrganizationGrades }) {
      const queryData = getOrganizationGradesFromCache({
        variables: getOrganizationGrades.variables,
      });

      const grades = _.get(queryData, "grades", []);

      return {
        grades,
        isLoading: _.includes(
          GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
          getOrganizationGrades.networkStatus
        ),
        isData: !_.isEmpty(queryData),
      };
    },
  }),
  graphql(getOrganizationSubjectsQuery, {
    name: "getOrganizationSubjects",
    skip: ({ curriculumProgramIds, shouldDisplaySubjectsFilter }) =>
      _.isEmpty(curriculumProgramIds) || !shouldDisplaySubjectsFilter,
    options: ({ organizationId, subjectQueryFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: organizationId,
        filters: subjectQueryFilters,
        portalType: "PLANNER",
      },
    }),
    props({ getOrganizationSubjects }) {
      const queryData = getOrganizationSubjectsFromCache({
        variables: getOrganizationSubjects.variables,
      });

      const subjects = _.get(queryData, "subjects", []);
      const subjectOptions = getSubjectOptionsMemoized({ subjects });

      return {
        subjects,
        subjectOptions,
        isLoading: _.includes(
          GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
          getOrganizationSubjects.networkStatus
        ),
        isData: !_.isEmpty(queryData),
      };
    },
  })
)(CourseFilters);
